import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { FlaskConicalIcon, Loader2Icon, MailCheckIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { Field } from '@ems/ui/patterns/field'

import { reloadThemeStylesheet } from '#admin/features/theme/stylesheet.ts'
import { establishSession } from '#admin/features/auth/queries.ts'
import {
  emailProblem,
  signInWithMagicLink,
  signInWithPassword,
} from '#admin/features/auth/sign-in.ts'
import { AuthSplitLayout } from '#admin/components/auth-split-layout.tsx'
import { TestAccountFlow } from '#admin/features/auth/test-account-flow.tsx'
import { testPersonasQuery } from '#admin/features/auth/test-personas.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * Sign in.
 *
 * A route, not a feature: it owns the URL, the page title and the form state. The requests
 * belong to `features/auth/sign-in.ts`, which is where the reason they post JSON — and what
 * happened when they did not — is written down.
 *
 * **Password first, magic link second**, which is the reverse of how this started. The original
 * offered only a magic link, on the reasoning that an operator console then needs no password
 * reset flow of its own — sound, and it assumes email works. Email is the part that does not:
 * SES is in sandbox until production access is granted, and a sandboxed account may only send to
 * *verified* addresses, so a link to a new operator is silently undeliverable. A console nobody
 * can enter is worse than one with a password field. Both are offered; `auth.ts` has had
 * `emailAndPassword` enabled all along, and `bootstrap-platform.ts` sets one for platform staff.
 */
export const Route = createFileRoute('/(public)/login')({
  component: LoginPage,
})

type Mode = 'password' | 'link'

function LoginPage() {
  const navigate = useNavigate()
  const router = useRouter()
  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  // The test-account flow replaces the form in place rather than opening beside it: it is a
  // different way in, and two sets of fields on one screen is how the wrong one gets filled in.
  const [view, setView] = useState<'form' | 'test'>('form')
  const { data: personas } = useQuery(testPersonasQuery())
  const offersTestAccounts = personas !== undefined && personas !== null && personas.length > 0
  const headingRef = useRef<HTMLHeadingElement>(null)

  // Back from the test-account flow: focus returns to the form's heading, not to the top of the page.
  const returning = useRef(false)
  useEffect(() => {
    if (view === 'form' && returning.current) headingRef.current?.focus()
    returning.current = view === 'test'
  }, [view])

  // `void`-returning, with the async work inside: an `onSubmit` handler that returns a promise
  // is a floating promise React never awaits, and a rejection there is an unhandled rejection
  // rather than an error this page can show.
  function submit(event: React.FormEvent) {
    event.preventDefault()
    void run()
  }

  async function run() {
    // Checked here, so a missing or malformed address is named at once and costs no request.
    const invalid = emailProblem(email)
    if (invalid !== null) {
      setError(invalid)
      return
    }

    setBusy(true)
    setError(null)

    const failure =
      mode === 'password'
        ? await signInWithPassword(email, password)
        : await signInWithMagicLink(email)

    if (failure !== null) {
      setBusy(false)
      setError(failure.message)
      return
    }

    if (mode === 'link') {
      setBusy(false)
      setSent(true)
      return
    }

    const problem = await finishSignIn()
    if (problem !== null) {
      setBusy(false)
      setError(problem)
    }
  }

  /**
   * Everything after the credentials were accepted — shared by the password form and the
   * test-account panel, so both land on the dashboard the same way. Resolves to an error message,
   * or to null once it has navigated.
   */
  async function finishSignIn(): Promise<string | null> {
    /**
     * The session query is already cached as "signed out" — the layout loader asked before
     * redirecting here — and the guard reads that answer and bounces straight back to this page,
     * which looks like the password was wrong.
     *
     * **Invalidating was not enough, and was the bug.** Nothing on this page observes the session
     * query, so `invalidateQueries` only marked it stale — and `ensureQueryData` in the layout's
     * loader returns cached data, stale or not. `establishSession` fetches afresh and is awaited,
     * so the answer is replaced before anything reads it.
     */
    const session = await establishSession(router.options.context.queryClient).catch(() => null)
    if ((session?.user ?? null) === null) {
      // The sign-in was accepted and still no session came back — almost always a cookie the
      // browser refused to keep. Saying so beats a login form that silently reloads itself.
      return 'You were signed in, but this browser did not keep the session. Allow cookies for this site and try again.'
    }
    await router.invalidate()
    await router.options.context.queryClient.invalidateQueries({
      queryKey: manifestQuery().queryKey,
    })

    /**
     * The console's tenant comes from the *session*, so the theme could not be fetched until now.
     *
     * `admin.eventsystem.id` belongs to no tenant — it is the platform's own hostname — and the
     * `<link>` in `index.html` was requested before anybody had signed in, so it 404'd and the
     * console painted in the fallback palette. This navigation is an SPA transition, so without
     * this line nothing asks again and the operator's edition stays unbranded until they reload
     * the page by hand.
     */
    reloadThemeStylesheet()

    // Straight to the dashboard rather than `/` and its redirect, and with `busy` still on, so the
    // button cannot send a second sign-in while the page changes.
    await navigate({ to: '/dashboard' })
    return null
  }

  if (sent) {
    return (
      <AuthSplitLayout>
        <div className="flex flex-col gap-4">
          <span
            aria-hidden="true"
            className="bg-primary/10 text-primary flex size-11 items-center justify-center rounded-full"
          >
            <MailCheckIcon className="size-5" />
          </span>
          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
            <p className="text-muted-foreground text-sm text-pretty">
              A sign-in link is on its way to <span className="text-foreground">{email}</span>. It
              works once and expires in fifteen minutes.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSent(false)
              setMode('password')
            }}
          >
            Back to sign in
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  if (view === 'test' && offersTestAccounts) {
    return (
      <AuthSplitLayout>
        <TestAccountFlow
          personas={personas}
          onBack={() => setView('form')}
          onSignedIn={finishSignIn}
        />
      </AuthSplitLayout>
    )
  }

  return (
    <AuthSplitLayout>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1.5">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-semibold tracking-tight outline-none"
          >
            Sign in
          </h1>
          <p className="text-muted-foreground text-sm text-pretty">
            {mode === 'password'
              ? 'Use the email address your edition was set up with.'
              : 'We will email you a link that signs you in — no password needed.'}
          </p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
          {/*
            The failure is attached to the field it is about rather than dropped under the submit
            button, which is where every form in this console used to put it. The server answers
            "Invalid email or password." for both, so it hangs off the email field — the first one,
            which is where focus goes and where somebody looking for the problem starts.
          */}
          <Field label="Email" {...(error === null ? {} : { error })}>
            {(field) => (
              <Input
                {...field}
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  // The message is about what was typed before. Once the address changes, it may
                  // no longer be true.
                  setError(null)
                }}
                disabled={busy}
              />
            )}
          </Field>

          {mode === 'password' && (
            <Field label="Password">
              {(field) => (
                <Input
                  {...field}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
              )}
            </Field>
          )}

          {/*
            Enabled until the request starts, rather than disabled until the form looks valid.
            A submit that cannot be pressed cannot explain why, and "the button is grayed out" is
            the single most common way somebody gets stuck on a login screen.
          */}
          <Button type="submit" disabled={busy} className="mt-1">
            {busy ? <Loader2Icon className="animate-spin" /> : null}
            {busy ? 'Working…' : mode === 'password' ? 'Sign in' : 'Email me a sign-in link'}
          </Button>

          <Button
            type="button"
            variant="link"
            className="self-center"
            onClick={() => {
              setMode(mode === 'password' ? 'link' : 'password')
              setError(null)
            }}
          >
            {mode === 'password' ? 'Email me a sign-in link instead' : 'Use a password instead'}
          </Button>
        </form>

        {/* Only on a stage that offers test accounts; everywhere else the page ends at the form. */}
        {offersTestAccounts ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-(--border)" />
              <span className="text-muted-foreground text-xs uppercase">or</span>
              <span className="h-px flex-1 bg-(--border)" />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setError(null)
                setView('test')
              }}
            >
              <FlaskConicalIcon />
              Sign in as a test account
            </Button>
          </div>
        ) : null}
      </div>
    </AuthSplitLayout>
  )
}
